import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaFileAlt, FaProjectDiagram, FaSearch } from 'react-icons/fa';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import {
  FiltersBar,
  StatsBar,
  StatCard,
  StatValue,
  StatLabel,
  AuditRow,
  ActionBadge,
  RowMain,
  RowHeader,
  RowTitle,
  RowMeta,
  MetaStrong,
  RowRight,
  Timestamp,
  ReasonBlock,
  ReasonLabel,
  DetailToggle,
  SnapshotPre,
  DeletedTag,
  LoadMoreButton
} from './styles';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import EmployeeSearch from '../../common/EmployeeSearch';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import ProjectSearch from '../../common/ProjectSearch';
import ReviewTypeFilter from '../../common/filters/ReviewType';
import { getReviewTypeText } from '../../../constants/reviewTypes';
import { PROJECT_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import useDebounce from '../../../hooks/useDebounce';
import { feedbackApi } from '../../../services/api/feedbackApi';
import { AUDIT_SCOPES, AUDIT_SCOPE_OPTIONS } from '../../../constants/feedbackModificationAuditScopes';

// Month options for the session month filter (1-12 as stored on the session).
const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

// Year options: a small window around the current year (sessions are scheduled
// close to "now"). Newest first.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR + 1 - i);

// Action codes mirror src/utils/feedbackSessionModificationActions.js (radar-api).
// Keep in sync if a new audit action is introduced backend-side.
const ACTION_LABELS = {
  'remove-assignee': 'Removed assignee',
  'replace-reviewer-transfer': 'Replaced reviewer (kept responses)',
  'replace-reviewer-wipe': 'Replaced reviewer (wiped responses)',
  'remove-reviewer': 'Removed reviewer',
  'change-matrix': 'Changed skills matrix',
  'reopen-actions': 'Reopened actions',
  'reopen-review': 'Reopened review',
  'reset-review': 'Reset review',
  'reopen-reviewer-actions': 'Reopened Start/Stop/Continue (interim step)',
  'add-assignee': 'Added assignee',
  'add-reviewer': 'Added reviewer',
  'switch-reviewer-order': 'Switched reviewer order',
  // ADR-192/193: a feedback session was deleted, with or without individuals
  // (destructive, reason required).
  'delete-session': 'Deleted session'
};

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  ...Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label }))
];

// Reviewer-scoped actions: the affected individual is a reviewer, and the
// assignee (the person being reviewed) is shown on a separate line.
const REVIEWER_ACTIONS = new Set([
  'add-reviewer',
  'remove-reviewer',
  'replace-reviewer-transfer',
  'replace-reviewer-wipe',
  // Switch-order is reviewer-scoped: the two swapped reviewers are the affected
  // individuals (shown as "Reviewer: X -> Y") and the assignee renders separately.
  'switch-reviewer-order'
]);

// Mirrors DESTRUCTIVE_AUDIT_ACTIONS in radar-api so the Action selector can use
// the same red/indigo badge colours the report rows use. Keep in sync.
const DESTRUCTIVE_ACTIONS = new Set([
  'remove-assignee',
  'replace-reviewer-wipe',
  'remove-reviewer',
  'change-matrix',
  'reopen-review',
  'reset-review',
  // ADR-192 §12
  'delete-session'
]);

const getActionLabel = (action) => ACTION_LABELS[action] || action;

const formatTimestamp = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

const renderEmployee = (employee) => {
  if (!employee) {
    return null;
  }
  if (employee.deleted || !employee.fullName) {
    return <DeletedTag>(removed individual)</DeletedTag>;
  }
  return <MetaStrong>{employee.fullName}</MetaStrong>;
};

const FeedbackModificationAuditReport = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pageInfo, setPageInfo] = useState({ cursor: null, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Filters
  const [action, setAction] = useState('');
  const [destructiveOnly, setDestructiveOnly] = useState(false);
  // ADR-106 §5: '' (all) | 'session' | 'individual'
  const [scope, setScope] = useState('');
  const [actor, setActor] = useState(null);
  const [affectedEmployee, setAffectedEmployee] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Session-attribute filters (mirror the fields shown on the session line).
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [reviewTypes, setReviewTypes] = useState([]);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [sessionNameInput, setSessionNameInput] = useState('');
  const [projectId, setProjectId] = useState('');
  // Debounce the free-text session name so we don't refetch on every keystroke.
  const sessionName = useDebounce(sessionNameInput, 500);

  const toggleReviewType = useCallback((reviewType) => {
    setReviewTypes(prev => (
      prev.includes(reviewType)
        ? prev.filter(t => t !== reviewType)
        : [...prev, reviewType]
    ));
  }, []);

  const filterParams = useCallback(() => ({
    ...(action && { action }),
    ...(destructiveOnly && { destructiveOnly: true }),
    ...(scope && { scope }),
    ...(actor && { actorEmployeeId: actor.id }),
    ...(affectedEmployee && { employeeId: affectedEmployee.id }),
    ...(dateFrom && dateFrom.isValid() && { dateFrom: dateFrom.startOf('day').toISOString() }),
    ...(dateTo && dateTo.isValid() && { dateTo: dateTo.endOf('day').toISOString() }),
    ...(businessUnitId && { businessUnitId }),
    ...(reviewTypes.length && { reviewType: reviewTypes }),
    ...(month && { month }),
    ...(year && { year }),
    ...(sessionName.trim() && { sessionName: sessionName.trim() }),
    ...(projectId && { projectId })
  }), [
    action, destructiveOnly, scope, actor, affectedEmployee, dateFrom, dateTo,
    businessUnitId, reviewTypes, month, year, sessionName, projectId
  ]);

  const fetchReport = useCallback(async (cursor = undefined, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const params = { limit: 50, orderDirection: 'desc', ...filterParams() };
      if (cursor) {
        params.cursor = cursor;
      }

      const result = await feedbackApi.getFeedbackModificationAuditReport(params);

      setItems(prev => (append ? [...prev, ...result.items] : result.items));
      setSummary(result.summary);
      setPageInfo(result.pageInfo || { cursor: null, hasMore: false });
    } catch (error) {
      console.error('Error fetching feedback modification audit report:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filterParams]);

  useEffect(() => {
    setExpandedRows(new Set());
    fetchReport();
  }, [fetchReport]);

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderFilters = () => (
    <FiltersBar>
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel>Action</InputLabel>
        <Select
          value={action}
          label="Action"
          onChange={(e) => setAction(e.target.value)}
          renderValue={(selected) => (
            selected
              ? (
                <ActionBadge $destructive={DESTRUCTIVE_ACTIONS.has(selected)}>
                  {getActionLabel(selected)}
                </ActionBadge>
              )
              : 'All actions'
          )}
        >
          {ACTION_OPTIONS.map((opt) => (
            <MenuItem key={opt.value || 'all'} value={opt.value}>
              {opt.value
                ? (
                  <ActionBadge $destructive={DESTRUCTIVE_ACTIONS.has(opt.value)}>
                    {opt.label}
                  </ActionBadge>
                )
                : opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={destructiveOnly}
            onChange={(e) => setDestructiveOnly(e.target.checked)}
            size="small"
          />
        }
        label="Destructive only"
      />

      {/* ADR-106 §5: scope — session-anchored vs individual-assignment rows */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Scope</InputLabel>
        <Select
          value={scope}
          label="Scope"
          onChange={(e) => setScope(e.target.value)}
          renderValue={(selected) => (
            (AUDIT_SCOPE_OPTIONS.find(o => o.value === selected) || AUDIT_SCOPE_OPTIONS[0]).label
          )}
        >
          {AUDIT_SCOPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value || 'all'} value={opt.value}>
              <Tooltip title={opt.description} placement="right" arrow>
                <span>{opt.label}</span>
              </Tooltip>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <EmployeeSearchProvider instanceId="audit-report-actor">
        <EmployeeSearch
          value={actor}
          onChange={setActor}
          label="Actor"
          placeholder="Filter by who made the change..."
          width={240}
          height={40}
          hideLabel
        />
      </EmployeeSearchProvider>

      <EmployeeSearchProvider instanceId="audit-report-affected">
        <EmployeeSearch
          value={affectedEmployee}
          onChange={setAffectedEmployee}
          label="Affected individual"
          placeholder="Filter by affected individual..."
          width={240}
          height={40}
          hideLabel
        />
      </EmployeeSearchProvider>

      <DatePicker
        label="From"
        value={dateFrom}
        onChange={setDateFrom}
        maxDate={dateTo || undefined}
        slotProps={{
          textField: { size: 'small', sx: { minWidth: 160 } },
          field: { clearable: true }
        }}
      />

      <DatePicker
        label="To"
        value={dateTo}
        onChange={setDateTo}
        minDate={dateFrom || undefined}
        slotProps={{
          textField: { size: 'small', sx: { minWidth: 160 } },
          field: { clearable: true }
        }}
      />

      {/* Session-attribute filters (mirror the session line) */}
      <TextField
        size="small"
        placeholder="Filter by session name..."
        value={sessionNameInput}
        onChange={(e) => setSessionNameInput(e.target.value)}
        sx={{ minWidth: 220 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <FaSearch style={{ color: '#94A3B8', fontSize: 13 }} />
              </InputAdornment>
            )
          }
        }}
      />

      <BusinessUnitSelector
        includeInactive // ADR-120: reports keep ended units, marked "Ended <date>"
        value={businessUnitId}
        onChange={(val) => setBusinessUnitId(val || '')}
        size="small"
        fullWidth={false}
        showEmptyOption
        emptyOptionLabel="All Business Units"
        label="Business Unit"
        showStructureType // ADR-119
        sx={{ minWidth: 200 }}
      />

      <ReviewTypeFilter
        selectedReviewTypes={reviewTypes}
        onReviewTypeToggle={toggleReviewType}
      />

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Month</InputLabel>
        <Select
          value={month}
          label="Month"
          onChange={(e) => setMonth(e.target.value)}
        >
          <MenuItem value="">All months</MenuItem>
          {MONTH_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={String(opt.value)}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Year</InputLabel>
        <Select
          value={year}
          label="Year"
          onChange={(e) => setYear(e.target.value)}
        >
          <MenuItem value="">All years</MenuItem>
          {YEAR_OPTIONS.map((y) => (
            <MenuItem key={y} value={String(y)}>{y}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <ProjectSearch
        value={projectId}
        onChange={(val) => setProjectId(val || '')}
        returnValue={PROJECT_RETURN_TYPES.ID}
        label="Project"
        placeholder="Filter by project..."
        width={340}
        showAssignmentIndicators={false}
      />
    </FiltersBar>
  );

  const renderStats = () => {
    if (!summary) {
      return null;
    }
    return (
      <StatsBar>
        <StatCard>
          <StatValue>{summary.total}</StatValue>
          <StatLabel>Changes</StatLabel>
        </StatCard>
        <StatCard $bgColor="#FEF2F2" $borderColor="#FECACA">
          <StatValue $color="#DC2626">{summary.destructiveCount}</StatValue>
          <StatLabel>Destructive</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{summary.distinctActors}</StatValue>
          <StatLabel>Actors</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{summary.distinctSessions}</StatValue>
          <StatLabel>Sessions</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{summary.distinctIndividualAssignments ?? 0}</StatValue>
          <StatLabel>Individual assignments</StatLabel>
        </StatCard>
      </StatsBar>
    );
  };

  const renderSessionLabel = (session) => {
    if (!session) {
      return <DeletedTag>(no session)</DeletedTag>;
    }
    if (!session.name) {
      return <DeletedTag>(removed session)</DeletedTag>;
    }
    // ADR-192: a session deleted through delete-session keeps its name from the
    // audit snapshot, tagged as deleted.
    if (session.deleted) {
      return (
        <>
          <MetaStrong>{session.name} ({session.month}/{session.year})</MetaStrong>{' '}
          <DeletedTag>(deleted)</DeletedTag>
        </>
      );
    }
    return <MetaStrong>{session.name} ({session.month}/{session.year})</MetaStrong>;
  };

  // The full session line mirrors the feedback-session card on a single row, in
  // order: business unit (business unit) • session template + version • session
  // type (plain text, no colour chip) • month • year • session name. Falls back
  // to the deleted/no-session tag when the session no longer resolves.
  const renderSessionLine = (session) => {
    if (!session || session.deleted || !session.name) {
      return renderSessionLabel(session);
    }

    const parts = [];

    if (session.businessUnit?.name) {
      parts.push(<MetaStrong key="bu">{session.businessUnit.name}</MetaStrong>);
    }

    if (session.sessionTemplate) {
      parts.push(
        <Box key="tpl" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <FaFileAlt size={12} />
          <MetaStrong>{session.sessionTemplate.name}</MetaStrong>
          {session.sessionTemplate.version != null && (
            <Chip
              label={`v${session.sessionTemplate.version}`}
              size="small"
              sx={{
                fontSize: '11px',
                height: '20px',
                backgroundColor: '#F1F5F9',
                color: '#475569',
                border: '1px solid #E2E8F0',
                fontWeight: 500,
              }}
            />
          )}
        </Box>
      );
    }

    if (session.reviewType) {
      parts.push(<span key="type">{getReviewTypeText(session.reviewType)}</span>);
    }

    if (session.month != null) {
      parts.push(<span key="month">{session.month}</span>);
    }

    if (session.year != null) {
      parts.push(<span key="year">{session.year}</span>);
    }

    parts.push(<MetaStrong key="name">{session.name}</MetaStrong>);

    return parts.map((part, index) => (
      <React.Fragment key={part.key}>
        {index > 0 && (
          <Box component="span" sx={{ color: '#CBD5E1', mx: 0.25 }}>&bull;</Box>
        )}
        {part}
      </React.Fragment>
    ));
  };

  // Projects line beneath the session, mirroring the feedback-session card:
  // the attached project chips, or "All projects included" when none.
  const renderSessionProjects = (session) => {
    if (!session || session.deleted || !session.name) {
      return null;
    }
    const projects = session.sessionProjects || [];
    return (
      <RowMeta>
        <FaProjectDiagram />
        {projects.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {projects.map((project) => (
              <Chip
                key={project.id}
                label={project.name}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '22px',
                  backgroundColor: '#F8FAFC',
                  color: '#64748B',
                  border: '1px solid #E2E8F0',
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '12px', fontStyle: 'italic', color: '#64748B' }}>
            All projects included
          </Typography>
        )}
      </RowMeta>
    );
  };

  const renderAffected = (item) => {
    const hasOld = item.oldEmployee;
    const hasNew = item.newEmployee;
    if (!hasOld && !hasNew) {
      return null;
    }
    const label = REVIEWER_ACTIONS.has(item.action) ? 'Reviewer' : 'Individual';
    return (
      <RowMeta>
        {label}:&nbsp;
        {hasOld && renderEmployee(item.oldEmployee)}
        {hasOld && hasNew && <span>&nbsp;&rarr;&nbsp;</span>}
        {hasNew && renderEmployee(item.newEmployee)}
      </RowMeta>
    );
  };

  const renderAssignee = (item) => {
    if (!item.assignee) {
      return null;
    }
    // ADR-106 §5: individual rows carry the assignee on the scope line.
    if (item.scope === AUDIT_SCOPES.INDIVIDUAL) {
      return null;
    }
    const assigneeId = item.assignee.employeeId;
    const oldId = item.oldEmployee?.employeeId;
    const newId = item.newEmployee?.employeeId;
    // Skip when the assignee is already the affected individual (e.g. add/remove-assignee).
    if (assigneeId && (assigneeId === oldId || assigneeId === newId)) {
      return null;
    }
    return (
      <RowMeta>
        Assignee:&nbsp;{renderEmployee(item.assignee)}
      </RowMeta>
    );
  };

  // The acted-on reviewer (reopen-review, Sequential reopen-
  // actions). Reviewer-scoped replace/remove/add/switch already surface the
  // reviewer via renderAffected (old/newEmployee), so skip those to avoid a
  // duplicate line. Also skip when the reviewer IS the assignee (self-assessment).
  const renderReviewer = (item) => {
    if (!item.reviewer) {
      return null;
    }
    if (item.oldEmployee || item.newEmployee) {
      return null;
    }
    if (item.assignee && item.reviewer.employeeId === item.assignee.employeeId) {
      return null;
    }
    return (
      <RowMeta>
        Reviewer:&nbsp;{renderEmployee(item.reviewer)}
      </RowMeta>
    );
  };

  const renderMatrixName = (matrix) => {
    if (!matrix) {
      return <DeletedTag>(none)</DeletedTag>;
    }
    if (!matrix.name) {
      return <DeletedTag>(removed matrix)</DeletedTag>;
    }
    return <MetaStrong>{matrix.name}</MetaStrong>;
  };

  const renderMatrixChange = (item) => {
    const change = item.skillsMatrixChange;
    if (!change) {
      return null;
    }
    return (
      <RowMeta>
        Skills matrix:&nbsp;
        {renderMatrixName(change.from)}
        <span>&nbsp;&rarr;&nbsp;</span>
        {renderMatrixName(change.to)}
      </RowMeta>
    );
  };

  const renderRows = () => {
    if (items.length === 0) {
      return (
        <EmptyState
          title="No modifications found"
          description="No feedback session modifications match the selected filters. Try adjusting your criteria."
        />
      );
    }

    return (
      <>
        {items.map((item) => (
          <AuditRow key={item.id}>
            <RowMain>
              <RowHeader>
                {item.actor && !item.actor.deleted && item.actor.fullName && (
                  <EmployeeAvatar
                    employee={{ azureId: item.actor.azureId, fullName: item.actor.fullName }}
                    size={28}
                  />
                )}
                <RowTitle>
                  {item.actor?.fullName || item.createdBy || 'Unknown actor'}
                </RowTitle>
              </RowHeader>

              {item.scope === AUDIT_SCOPES.INDIVIDUAL
                ? (
                  <RowMeta>
                    Individual assignment{item.assignee ? <>&nbsp;·&nbsp;{renderEmployee(item.assignee)}</> : null}
                  </RowMeta>
                )
                : (
                  <RowMeta>
                    Session: {renderSessionLine(item.session)}
                  </RowMeta>
                )}

              {item.scope !== AUDIT_SCOPES.INDIVIDUAL && renderSessionProjects(item.session)}

              {renderAssignee(item)}

              {renderReviewer(item)}

              {renderAffected(item)}

              {renderMatrixChange(item)}

              {item.reason && (
                <ReasonBlock>
                  <ReasonLabel>Reason</ReasonLabel>
                  {item.reason}
                </ReasonBlock>
              )}

              {(item.hasBeforeSnapshot || item.hasAfterSnapshot || item.metadata) && (
                <>
                  <DetailToggle onClick={() => toggleRow(item.id)}>
                    {expandedRows.has(item.id) ? 'Hide details' : 'Show details'}
                  </DetailToggle>
                  {expandedRows.has(item.id) && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        {item.hasBeforeSnapshot ? 'Before-snapshot captured. ' : ''}
                        {item.hasAfterSnapshot ? 'After-snapshot captured.' : ''}
                      </Typography>
                      {item.metadata && (
                        <SnapshotPre>{JSON.stringify(item.metadata, null, 2)}</SnapshotPre>
                      )}
                    </Box>
                  )}
                </>
              )}
            </RowMain>

            <RowRight>
              <ActionBadge $destructive={item.isDestructive}>
                {getActionLabel(item.action)}
              </ActionBadge>
              <Timestamp>{formatTimestamp(item.createdAt)}</Timestamp>
            </RowRight>
          </AuditRow>
        ))}

        {pageInfo.hasMore && (
          <LoadMoreButton
            onClick={() => fetchReport(pageInfo.cursor, true)}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </LoadMoreButton>
        )}
      </>
    );
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Feedback Modification Audit</Title>
        </HeaderLeft>
        {renderStats()}
      </Header>

      <Content>
        <MainSection>
          {renderFilters()}

          {!loading && (
            <ResultsCount count={items.length} singular="modification" plural="modifications" />
          )}

          {loading ? (
            <LoadingSpinner message="Loading modification audit report..." />
          ) : (
            renderRows()
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default FeedbackModificationAuditReport;
